import * as React from 'react';
import { connect } from 'react-redux';
import Router, { withRouter } from 'next/router';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import { CSVLink } from 'react-csv';
import Link from 'next/link';
import { Select, FormControl, MenuItem } from '@material-ui/core';
import { Share } from '@material-ui/icons';

import PageBase from '../../components/layout/PageBase';
import BlurOverlay from '../../components/utility/BlurOverlay';
import ToolsHeader from '../../components/pages/tools/header/index';
import LocationForm from '../../components/utility/LocationForm';
import ButtonWithSnackBar from '../../components/utility/SnackBarButton';
import ReactTable from '../../components/pages/monthly-market/MonthlyMarketTable';
import { normalizePrice, percentage } from '../../lib/helpers';
import {
  fetchMonthlyMarketData,
  fetchMonthlyMarketMonths,
} from '../../actions/monthlyMarket';
import BackButton from '../../components/utility/BackButton';
import track from '../../lib/track';
import * as types from '../../actions/actionTypes';
import style from '../../styles/sass/pages/MonthlyData.module.scss';
import { TextField } from '@material-ui/core';
import { debounce } from 'lodash';

const styles = {
  downloadIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10,
    marginTop: 20,
  },
  formControl: {
    marginRight: 15,
  },
  icon: {
    fill: 'white',
  },
  select: {
    '&:after': {
      borderColor: 'white',
    },
    '&:before': {
      borderColor: 'white',
    },
    color: 'white',
    fontSize: 36,
  },
  selectOption: {
    fontSize: 16,
    height: 20,
    lineHeight: 20,
    paddingBottom: 10,
    paddingTop: 10,
  },
  shareContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
};

export const MONTHLY_MARKET_LOCATIONS = {
  boston: 'Greater Boston Area',
};

export interface Props {
  area: string;
  availableMonths: any[];
  classes: any;
  dispatch: any;
  fetchMonthlyMarketData: Function;
  fetchMonthlyMarketMonths: Function;
  month: {
    data: any[];
  };
  router: any;
  token: string;
}

export interface State {
  area: string;
  monthCode: string;
  showCard: boolean;
  currentLocation: string;
  currentZipCode: string;
  dataToUse: any;
}

export class MonthlyData extends React.Component<Props, State> {
  supportsCopy =
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    navigator.clipboard.writeText;
  constructor(props: any) {
    super(props);
    // If we don't pass a month to the page, default to last month
    const lastMonth = moment(new Date())
      .startOf('month')
      .subtract(1, 'month')
      .format('YYYYMM');
    // If we've passed a "month" to the path in the form of
    let monthCodeToUse;
    let areaToUse;
    const { area, month } = props.router.query;
    if (month && !month.startsWith('20')) {
      // Support MMYYYY (ex. 032020)
      monthCodeToUse = `${month.slice(2)}${month.slice(0, 2)}`;
    } else if (month && month.startsWith('20')) {
      // Support YYYYMM (ex. 202003)
      monthCodeToUse = `${month.slice(0, -2)}${month.slice(month.length - 2)}`;
    }
    if (area && Object.keys(MONTHLY_MARKET_LOCATIONS).includes(area)) {
      areaToUse = area;
    }
    if (areaToUse && monthCodeToUse) {
      this.fetchData(areaToUse, monthCodeToUse);
    }
    this.state = {
      area: areaToUse || this.props.area,
      monthCode: monthCodeToUse || lastMonth,
      showCard: areaToUse && monthCodeToUse ? false : true,
      currentLocation: '',
      currentZipCode: '',
      dataToUse: null,
    };
    this.filterSearchDebounced = debounce(this.filterSearchDebounced, 500);
  }

  fetchData = (area: string, monthCode: string, changeURL?: boolean) => {
    this.props.fetchMonthlyMarketMonths(area);
    this.props.fetchMonthlyMarketData(monthCode, area);
    if (changeURL) {
      Router.push({
        pathname: '/monthly-market',
        query: {
          area,
          month: monthCode,
        },
      });
    }
  };

  onSubmit = (area: string) => {
    track.toolUsed('Monthly Market', { location: area });
    this.setState({
      area,
    });
    if (this.props.token) {
      this.fetchData(area, this.state.monthCode, true);
    } else {
      this.props.dispatch({
        postAuthPath: this.props.router.pathname,
        signupSubtext: `Sign up for full access to monthly market data.`,
        type: types.OpenSignupDialog,
      });
    }
    if (this.state.showCard) {
      this.setState({
        showCard: false,
      });
    }
  };

  setMonth = (event: any) => {
    this.setState(
      {
        monthCode: event.target.value,
      },
      () => {
        track.toolUsed('Monthly Market', { month: event.target.value });
        Router.push({
          pathname: '/monthly-market',
          query: {
            area: this.state.area,
            month: event.target.value,
          },
        });
        this.props.fetchMonthlyMarketData(event.target.value, this.state.area);
      },
    );
  };

  monthsToShow = () => {
    if (this.props.availableMonths && this.props.availableMonths.length > 0) {
      const monthsToShow = this.props.availableMonths.map((month: any) => {
        return (
          <MenuItem
            className={this.props.classes.selectOption}
            key={month.period}
            value={month.period}
          >
            {month.name}
          </MenuItem>
        );
      });

      return monthsToShow;
    }
    return null;
  };

  shareSearch = () => {
    const uri = encodeURI(window.location.href);
    navigator.clipboard.writeText(uri);
  };

  resetFilters = () => {
    this.setState({
      dataToUse: null,
      currentLocation: '',
      currentZipCode: '',
    });
  };

  filterSearchDebounced = () => {
    const { month } = this.props;
    if (!this.state.currentLocation && !this.state.currentZipCode) {
      this.resetFilters();
      return;
    }
    let temp: any = month.data.filter((element: any) => {
      if (this.state.currentLocation === '') {
        return (
          element.zipCode.slice(0, this.state.currentZipCode.length) ===
          this.state.currentZipCode
        );
      } else {
        return (
          element.town
            .slice(0, this.state.currentLocation.length)
            .toLowerCase() === this.state.currentLocation.toLowerCase()
        );
      }
    });
    this.setState({
      dataToUse: temp,
    });
  };

  handleFilterSearch = (e: any) => {
    const locationValue = e.target.value;
    if (!locationValue) {
      this.resetFilters();
    } else {
      if (!isNaN(locationValue)) {
        this.setState({ currentZipCode: locationValue });
      } else {
        this.setState({ currentLocation: locationValue });
      }
      this.filterSearchDebounced();
    }
  };

  render() {
    const { availableMonths, classes, month, token, router } = this.props;
    const columns = [
      {
        Cell: (props: any) => (
          <span>
            {props.value !== '0'
              ? `${props.value} (${props.cell.row.original.town})`
              : 'All'}
          </span>
        ),
        Header: 'ZIP CODE',
        accessor: 'zipCode',
        width: 200,
        maxWidth: 200,
      },
      {
        Header: 'DAYS ON MARKET',
        accessor: 'medianMarketTime',
        width: 153,
        maxWidth: 153,
      },
      {
        Cell: (props: any) => <span>${normalizePrice(props.value)}</span>,
        Header: 'LIST PRICE',
        accessor: 'medianListPrice',
        width: 125,
        maxWidth: 125,
      },
      {
        Cell: (props: any) => <span>${normalizePrice(props.value)}</span>,
        Header: 'SALE PRICE',
        accessor: 'medianSalePrice',
        width: 125,
        maxWidth: 125,
      },
      {
        Cell: (props: any) => <span>{percentage(props.value, 0)}</span>,
        Header: 'AVG. DELTA',
        accessor: 'delta',
        width: 110,
        maxWidth: 110,
      },
      {
        Cell: (props: any) => <span>${normalizePrice(props.value)}</span>,
        Header: '$/SQ FT',
        accessor: 'medianPricePerSf',
        width: 110,
        maxWidth: 110,
      },
      {
        Cell: (props: any) => <span>{normalizePrice(props.value)}</span>,
        Header: '# OF SALES',
        accessor: 'count',
        width: 110,
        maxWidth: 110,
      },
    ];

    return (
      <PageBase title="Torii Monthly Market - Monthly sales data and real estate trends">
        <div className={`${style.monthlyDataOuter} tool-container`}>
          <BackButton goBack={() => router.push('/tools')} />
          <ToolsHeader
            title="Monthly Market"
            slug="monthly-market"
            subTitle="Monthly sales data and real estate trends"
          />

          <LocationForm
            disabled={!token}
            hideCityField
            initialState={this.state.area}
            locationsOverride={MONTHLY_MARKET_LOCATIONS}
            onSubmit={this.onSubmit}
            showCard={this.state.showCard}
          />

          {this.props.token && month.data && month.data.length > 0 && (
            <div className={style.filtersContainer}>
              <TextField
                onChange={this.handleFilterSearch}
                placeholder="Enter city/town name or pincode"
                className={style.filterInput}
                disabled={!month.data || month.data.length === 0}
              />
            </div>
          )}

          {!this.state.showCard && (
            <div className={style.monthlyDataInner}>
              {!token && <BlurOverlay top={false} />}
              <div className={style.tableContainer}>
                <div
                  className={`col-xs-12 row center-xs middle-xs ${style.tableHeader}`}
                >
                  {availableMonths && availableMonths.length > 0 && (
                    <FormControl className={classes.formControl}>
                      <Select
                        className={classes.select}
                        displayEmpty
                        inputProps={{
                          classes: {
                            icon: classes.icon,
                          },
                        }}
                        name="monthCodeSelect"
                        onChange={this.setMonth}
                        value={this.state.monthCode}
                      >
                        {this.monthsToShow()}
                      </Select>
                    </FormControl>
                  )}
                </div>
                <div className={style.tableWrap}>
                  {month && month.data && (
                    <ReactTable
                      data={
                        this.state.dataToUse ? this.state.dataToUse : month.data
                      }
                      columns={columns}
                      pageSizeOptions={[10, 15, 20, 25, 50, 100]}
                      defaultPageSize={15}
                      defaultSorted={[
                        {
                          desc: false,
                          id: 'zipCode',
                        },
                      ]}
                    />
                  )}
                </div>
              </div>
              {month && month.data && (
                <div style={styles.downloadIconContainer}>
                  <CSVLink
                    data={month.data}
                    filename={`Torii Monthly Market - ${moment(
                      this.state.monthCode,
                      'YYYYMM',
                    ).format('MMMM YYYY')}.csv`}
                    target="_blank"
                    style={{ textDecoration: 'none' }}
                  >
                    <img
                      style={{ height: 12, width: 12 }}
                      data-alt="download"
                      src="/static/img/DL.svg"
                    />{' '}
                    Download .csv
                  </CSVLink>
                </div>
              )}
              {this.supportsCopy && (
                <div style={styles.shareContainer}>
                  <ButtonWithSnackBar
                    onSubmit={this.shareSearch}
                    buttonTitle={'Share'}
                    buttonIcon={
                      <Share style={{ fontSize: 16, marginRight: 5 }} />
                    }
                    variant="success"
                    snackBarMessage="Copied link to clipboard"
                    buttonSize="small"
                    buttonColor="primary"
                    buttonStyle={{ fontSize: 12 }}
                  />
                </div>
              )}
              <div className={`row ${style.monthlyMarketInfo}`}>
                <div className="col-sm-12 ">
                  <p className={style.infoTextStyle}>
                    The Monthly Market report is for informational use and
                    aggregates real estate market data based on closed home
                    sales. Some zip codes may have a low number of sales per
                    month, which could skew the results. For a full picture of
                    how the real estate market looks by zip code, look back at
                    least a few months to see how data like list prices, sale
                    prices, and days on market are trending. If you are
                    interested in using this real estate market news to price a
                    property, you can also try the{' '}
                    <Link href="/tools/comps-chomper">
                      <a target="_blank">Torii Comps Chomper</a>
                    </Link>
                    .
                  </p>
                  <p className={[style.infoTextStyle, style.bold].join(' ')}>
                    More information on Torii’s Monthly Market report and real
                    estate market news:
                  </p>
                  <ul className={style.listStyle}>
                    <li className={style.infoTextStyle}>
                      Torii Blog:
                      <a
                        href="https://blog.toriihomes.com/category/real-estate-market-news/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {' '}
                        Real Estate Market News
                      </a>
                    </li>
                    <li className={style.infoTextStyle}>
                      Monthly recaps of the
                      <a
                        href="https://blog.toriihomes.com/tag/monthly-market-report/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {' '}
                        Monthly Market Report
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </PageBase>
    );
  }
}

const mapStateToProps = (state: any) => ({
  area: state.monthlyData.area,
  availableMonths: state.monthlyData.availableMonths,
  month: state.monthlyData.month,
  token: state.auth.token,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
  fetchMonthlyMarketData(month: string, area: string) {
    dispatch(fetchMonthlyMarketData(month, area));
  },
  fetchMonthlyMarketMonths(area: string) {
    dispatch(fetchMonthlyMarketMonths(area));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(withRouter(MonthlyData)));
